import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Select from 'react-select';

import './tabel-rows.scss';

import { useIsManager } from '~modules/auth/model';
import { useLoadScheduleNames } from '~modules/schedule/use-load-schedule-names';
import { SectionDto } from '~modules/section/model';
import { useLoadPositions } from '~modules/staffing/use-load-positions';
import { TableFilter } from '~modules/tabel/api';
import DeviationEnum from '~modules/tabel/interfaces/deviation';
import Status from '~modules/tabel/interfaces/status';
import { WorkTimeTypes } from '~modules/tabel/interfaces/work-time-types';
import { TabelRowDto } from '~modules/tabel/model/tabel-row.dto';
import { useLoadAvailableSections } from '~pages/cabinet/admin/sections/SectionList/use-load-available-sectionds';
import { debounce } from '~shared/helpers';
import { routerKeys } from '~shared/router';
import { CheckBox } from '~shared/ui';
import DateIntervalPicker from '~shared/ui/date-interval-picker/date-interval-picker';
import { InputItem } from '~shared/ui/input-item/input-item';
import { ITableHeader } from '~shared/ui/table/table';

import { StaffingSelect } from '../staffing-select/staffing-select';
import { WorkTimeTypeSelect } from '../work-time-type-select/work-time-type-select';
import Comment from './comment-input/comment';
import { DurationInput } from './duration-input/duration-input';
import TabelTip from './tabel-tip/tabel-tip';
import { TimeRange } from './time-range-input/time-range';

export function useHeaders(
	selectedRows: Set<number>,
	_isSelectedAll: boolean,
	filter: TableFilter,
	onTurnRowSelection: (id: number) => void,
	_onTurnSelectionOfAll: () => void,
	onChangeFilter: (filter: Partial<TableFilter>) => void,
	onSave: (row: TabelRowDto) => void,
	onEdit: () => void,
): ITableHeader[] {
	const { list: sections, isPending: pendingSections } = useLoadAvailableSections();
	const { list: scheduleNames, isPending: pendingScheduleNames } = useLoadScheduleNames();
	const { list: positions, isPending: pendingPositions } = useLoadPositions(filter.section_id);

	const isManager = useIsManager();
	const [changedRows, setChangedRows] = useState<Set<number>>(new Set());

	const markRow = useCallback(
		(id: number) => {
			setChangedRows(state => {
				const nextState = new Set(state);
				nextState.add(id);
				return nextState;
			});
			onEdit();
		},
		[onEdit],
	);

	const unmarkRow = useCallback((id: number) => {
		setChangedRows(state => {
			const nextState = new Set(state);
			nextState.delete(id);
			return nextState;
		});
	}, []);

	const debouncedSave = useMemo(
		() =>
			debounce((row: TabelRowDto) => {
				unmarkRow(row.id);
				onSave(row);
			}, 2000),
		[unmarkRow, onSave],
	);

	const headers: ITableHeader[] = [
		{
			value: 'employees_name',
			width: '250px',
			formatted: useCallback(
				(row: TabelRowDto) => {
					const tipParams: { fill: 'info' | null; description: string } = row.expired
						? { fill: 'info', description: 'Данная строка выходит за пределы табелирования' }
						: { fill: null, description: '' };

					return (
						<div className={`checkbox-cell ${changedRows.has(row.id) ? 'edited' : ''}`}>
							<div className="checkbox-container">
								<TabelTip type={tipParams.fill} message={tipParams.description}>
									<CheckBox
										disabled={row.expired}
										checked={selectedRows.has(row.id)}
										onChange={() => onTurnRowSelection(row.id)}
									/>
								</TabelTip>
							</div>
							<div>
								{row.user?.id ? (
									<Link to={routerKeys.tabel.attendance(row.user.id)}>{row.user.name}</Link>
								) : (
									row.user?.name
								)}
							</div>
						</div>
					);
				},
				[changedRows, onTurnRowSelection, selectedRows],
			),
			filter: useCallback(() => {
				return (
					<div className="user-filter" style={{ display: 'flex', alignItems: 'center' }}>
						{/* <CheckBox checked={isSelectedAll} onChange={onTurnSelectionOfAll} /> */}
						<InputItem
							variant="ghost"
							placeholder="Сотрудник"
							value={filter.employees_name}
							onChange={e => {
								onChangeFilter({ employees_name: e.target.value });
							}}
						/>
					</div>
				);
			}, [filter.employees_name, onChangeFilter]),
		},
		{
			value: 'position',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>Должность</span>
				</div>
			),
			filter: useCallback(() => {
				return (
					<Select
						options={positions.map(v => ({ value: v.id, label: v.name }))}
						isClearable={true}
						defaultInputValue=""
						isDisabled={positions.length === 1}
						placeholder="Должность"
						isLoading={pendingPositions}
						onChange={v => {
							onChangeFilter({ position_id: v?.value });
						}}
					/>
				);
			}, [onChangeFilter, positions, pendingPositions]),
			width: '180px',
			formatted: useCallback(
				(row: TabelRowDto) => {
					const sectionId = row.user?.section_id;
					const departmentId = row.user?.section?.department_id;
					const organizationId = row.user?.section?.organization_id;

					return (
						<div>
							{sectionId && departmentId && organizationId && (
								<StaffingSelect
									defaultValue={row.staffing}
									defaultOptions={row.staffing ? [row.staffing] : []}
									isDisabled={row.status === Status.COMPLETED || row.expired}
									onChange={staff => {
										onSave({ ...row, staffing: staff, staffing_id: staff.id });
									}}
									sectionId={sectionId}
									departmentId={departmentId}
									organizationId={organizationId}
								/>
							)}
						</div>
					);
				},
				[onSave],
			),
		},
		{
			value: 'schedule_name',
			align: 'center',
			width: '100px',
			title: <div>Расписание</div>,
			filter: useCallback(() => {
				return (
					<Select
						options={scheduleNames.map(v => ({ value: v, label: v }))}
						isClearable={true}
						defaultInputValue=""
						isDisabled={scheduleNames.length === 1}
						placeholder="Тип"
						isLoading={pendingScheduleNames}
						onChange={v => {
							onChangeFilter({ schedule_name: v?.value });
						}}
					/>
				);
			}, [onChangeFilter, pendingScheduleNames, scheduleNames]),
			formatted: useCallback((row: TabelRowDto) => {
				return <div>{row.employee_schedule_plan?.schedule_name}</div>;
			}, []),
		},
		{
			value: 'users_section',
			align: 'left',
			width: '105px',
			title: (
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
					<span>Участок</span>
				</div>
			),
			formatted: useCallback((row: TabelRowDto) => {
				const title = row.user?.section?.title;
				return (
					<TabelTip message={title}>
						<div className="ellipsis-cell">{title}</div>
					</TabelTip>
				);
			}, []),
			filter: useCallback(() => {
				return (
					<Select<SectionDto>
						getOptionLabel={option => `${option.title}`}
						getOptionValue={option => `${option.id}-${option.id}`}
						options={sections}
						isClearable={true}
						value={sections.length === 1 ? sections[0] : undefined}
						isDisabled={sections.length === 1}
						placeholder="Участок"
						isLoading={pendingSections}
						onChange={v => onChangeFilter({ section_id: v?.id ?? undefined })}
					/>
				);
			}, [onChangeFilter, pendingSections, sections]),
		},
		{
			value: 'date',
			align: 'center',
			width: '90px',
			title: (
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>Дата</span>
				</div>
			),
			formatted: useCallback((row: TabelRowDto) => {
				return <div>{row.date ? moment(row.date).format('DD.MM.YYYY') : ''}</div>;
			}, []),
			filter: useCallback(() => {
				return (
					<div className="date-filter">
						<DateIntervalPicker
							startDate={filter.startDate}
							endDate={filter.endDate}
							onChange={(startDate, endDate) => {
								onChangeFilter({
									startDate: startDate ?? undefined,
									endDate: endDate ?? undefined,
								});
							}}
						/>
					</div>
				);
			}, [filter.startDate, filter.endDate, onChangeFilter]),
		},
		{
			value: 'work_time_type',
			width: '110px',
			align: 'center',
			formatted: useCallback(
				(row: TabelRowDto) => {
					if (!row?.work_time_type) return;
					return (
						<WorkTimeTypeSelect
							defaultValue={row.work_time_type}
							isDisabled={
								row.status === Status.COMPLETED ||
								row.expired ||
								[WorkTimeTypes.SickLeave, WorkTimeTypes.AnnualPaidLeave].includes(row.work_time_type)
							}
							isManager={isManager}
							excluded={[WorkTimeTypes.SickLeave, WorkTimeTypes.AnnualPaidLeave, WorkTimeTypes.Dismissed]}
							onChange={wtt => {
								if (wtt) onSave({ ...row, work_time_type: wtt });
							}}
						/>
					);
				},
				[isManager, onSave],
			),
			filter: useCallback(() => {
				return (
					<WorkTimeTypeSelect
						isManager={true}
						isClearable={true}
						onChange={v => onChangeFilter({ work_time_type: v })}
						isDisabled={false}
					/>
				);
			}, [onChangeFilter]),
		},
		{
			value: 'duration',
			width: '30px',
			align: 'center',
			title: (
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
					<span>Итог</span>
				</div>
			),
			formatted: useCallback(
				(row: TabelRowDto) => {
					const validTypes = [
						DeviationEnum.full_time_limit_exceeded,
						DeviationEnum.overworked_hours,
						DeviationEnum.part_time_limit_exceeded,
						DeviationEnum.underworked_hours,
					];

					const tipParams =
						row.status !== Status.COMPLETED &&
						!row.expired &&
						row.deviation?.type !== undefined &&
						row.deviation?.type !== null &&
						validTypes.includes(row.deviation?.type)
							? { fill: row.deviation?.criticality, description: row.deviation.description }
							: { fill: null, description: null };

					return (
						<TabelTip type={tipParams.fill} message={tipParams.description}>
							<DurationInput
								className={`duration duration-${tipParams.fill}`}
								defaultValue={row.duration}
								disabled={row.status === Status.COMPLETED || row.expired}
								onChange={value => {
									if (!changedRows.has(row.id)) markRow(row.id);
									debouncedSave({ ...row, duration: value });
								}}
								onBlur={value => {
									debouncedSave.cancel();
									unmarkRow(row.id);
									onSave({ ...row, duration: value });
								}}
							/>
						</TabelTip>
					);
				},
				[changedRows, debouncedSave, markRow, onSave, unmarkRow],
			),
		},
		{
			value: 'scheduled_time',
			width: '75px',
			title: (
				<div style={{ display: 'flex', minWidth: '110px', alignItems: 'center' }}>
					<span>По плану</span>
				</div>
			),
			formatted: useCallback((row: TabelRowDto) => {
				return (
					<TimeRange
						className="datetime-cell"
						begin={row.employee_schedule_plan?.start}
						end={row.employee_schedule_plan?.end}
						disabled={row.status === Status.COMPLETED || row.expired}
						withMinutes={false}
					/>
				);
			}, []),
		},
		{
			value: 'time',
			width: '105px',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span>По факту</span>
				</div>
			),
			formatted: useCallback((row: TabelRowDto) => {
				const validTypes = [DeviationEnum.scheduled_leave];

				const tipParams =
					row.status !== Status.COMPLETED &&
					!row.expired &&
					row.deviation?.type !== undefined &&
					row.deviation?.type !== null &&
					validTypes.includes(row.deviation.type)
						? { fill: row.deviation?.criticality, description: row.deviation.description }
						: { fill: null, description: null };

				return (
					<TabelTip type={tipParams.fill} message={tipParams.description}>
						<TimeRange
							className={`datetime-cell time-${tipParams.fill}`}
							begin={row.start}
							end={row.end}
							disabled={row.status === Status.COMPLETED || row.expired}
						/>
					</TabelTip>
				);
			}, []),
		},
		{
			value: 'comment',
			width: '240px',
			formatted: useCallback(
				(row: TabelRowDto) => {
					return (
						<Comment
							className={`comment-filter`}
							defaultValue={row.comment ?? null}
							disabled={row.status === Status.COMPLETED || row.expired}
							onChange={value => {
								if (!changedRows.has(row.id)) markRow(row.id);
								debouncedSave({ ...row, comment: value });
							}}
							onBlur={value => {
								debouncedSave.cancel();
								unmarkRow(row.id);
								onSave({ ...row, comment: value });
							}}
						/>
					);
				},
				[changedRows, debouncedSave, markRow, onSave, unmarkRow],
			),
		},
		{
			value: 'responsible',
			width: '120px',
			formatted: useCallback((row: TabelRowDto) => {
				return <div>{row.responsible?.name}</div>;
			}, []),
		},
	];

	return headers;
}
